'use client'

import Link from 'next/link'
import Image from 'next/image'
import { useState } from 'react'
import { Menu, X, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { siteConfig } from '@/lib/site-config'
import { siteNavigation } from '@/lib/site-pages'
import { cn } from '@/lib/utils'

export function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
  const [mobileSubmenuOpen, setMobileSubmenuOpen] = useState<string | null>(null)

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      {/* Floating nav container with margins */}
      <div className="mx-4 lg:mx-8 mt-4">
        <nav 
          className="mx-auto max-w-7xl px-3 sm:px-6 lg:px-8 py-3 rounded-full bg-white/50 backdrop-blur-xl border border-black/5 shadow-sm"
          aria-label="Main navigation"
        >
          <div className="flex items-center justify-between">
            {/* Logo */}
            <Link href="/" className="flex-shrink-0">
              <Image
                src="/images/mav-logo.png"
                alt="Michigan Aviation"
                width={684}
                height={232}
                sizes="120px"
                className="h-8 w-auto sm:h-10"
                priority
              />
            </Link>

            {/* Desktop Navigation - Centered */}
            <div className="hidden xl:flex xl:items-center xl:gap-8">
              {siteNavigation.map((item) =>
                'children' in item ? (
                  <details
                    key={item.name}
                    className="relative group"
                    onKeyDown={(event) => {
                      if (event.key === 'Escape') {
                        event.currentTarget.open = false
                        event.currentTarget.querySelector('summary')?.focus()
                      }
                    }}
                    onBlur={(event) => {
                      if (!event.currentTarget.contains(event.relatedTarget)) event.currentTarget.open = false
                    }}
                  >
                    <summary className="flex cursor-pointer list-none items-center gap-1 text-xs font-medium text-foreground/70 hover:text-foreground transition-colors tracking-wide [&::-webkit-details-marker]:hidden">
                      {item.name}
                      <ChevronDown aria-hidden="true" className="h-3 w-3 transition-transform group-open:rotate-180" />
                    </summary>
                    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3">
                      <ul className="flex max-h-[calc(100dvh-7rem)] w-64 flex-col gap-1 overflow-y-auto rounded-2xl bg-background border border-border shadow-lg p-3">
                        {item.children.map((child) => (
                          <li key={child.href}>
                            <Link
                              href={child.href}
                              onClick={(event) => { event.currentTarget.closest('details')?.removeAttribute('open') }}
                              className="block rounded-lg px-4 py-3 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-secondary focus-visible:bg-secondary transition-colors tracking-wide"
                            >
                              {child.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </details>
                ) : (
                  <Link
                    key={item.name}
                    href={item.href}
                    className="text-xs font-medium text-foreground/70 hover:text-foreground transition-colors tracking-wide"
                  >
                    {item.name}
                  </Link>
                )
              )}
            </div>

            {/* Contact stays visible when the mobile menu is closed. */}
            <div className="ml-auto flex items-center gap-2 xl:ml-0">
              <Button
                asChild
                size="sm"
                className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 text-xs font-medium tracking-wide px-5 py-2 h-auto"
              >
                <a href={`tel:${siteConfig.contact.phoneRaw}`}>Contact Us</a>
              </Button>

            {/* Mobile menu button */}
            <button
              type="button"
              className="xl:hidden p-2 -mr-2 text-foreground/80 hover:text-foreground transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileMenuOpen}
              aria-controls="mobile-navigation"
            >
              {mobileMenuOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>
            </div>
          </div>
        </nav>
      </div>

      {/* Mobile menu */}
      <div
        id="mobile-navigation"
        hidden={!mobileMenuOpen}
        className="xl:hidden max-h-[calc(100dvh-7rem)] overflow-y-auto mx-4 mt-2"
      >
        <div className="rounded-2xl bg-white/90 backdrop-blur-xl border border-black/5 shadow-lg px-6 py-6">
          <div className="flex flex-col gap-1">
            {siteNavigation.map((item) =>
              'children' in item ? (
                <div key={item.name}>
                    <button
                      type="button"
                      className="flex w-full items-center justify-between py-3 text-sm font-medium text-foreground/80 hover:text-foreground transition-colors"
                      onClick={() =>
                        setMobileSubmenuOpen(mobileSubmenuOpen === item.name ? null : item.name)
                      }
                      aria-expanded={mobileSubmenuOpen === item.name}
                      aria-controls="mobile-maintenance-links"
                    >
                      {item.name}
                      <ChevronDown
                        aria-hidden="true"
                        className={cn(
                          'h-4 w-4 transition-transform',
                          mobileSubmenuOpen === item.name && 'rotate-180'
                        )}
                      />
                    </button>
                  <div id="mobile-maintenance-links" hidden={mobileSubmenuOpen !== item.name}>
                    {item.children.map((child) => (
                      <Link
                        key={child.href}
                        href={child.href}
                        className="block pl-4 py-2.5 text-sm text-foreground/70 hover:text-foreground transition-colors"
                        onClick={() => setMobileMenuOpen(false)}
                      >
                        {child.name}
                      </Link>
                    ))}
                  </div>
                </div>
              ) : (
                <Link
                  key={item.name}
                  href={item.href}
                  className="text-foreground/80 hover:text-foreground py-3 text-sm font-medium transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
